import gql from 'graphql-tag';
import React, {useState} from 'react';
import {Button, Col, Modal, Row} from 'react-bootstrap';
import {Link} from 'react-router-dom';
import {GqlQueryRender} from 'shared/gql/gqlQueryRender';
import {AdminRoutes} from 'shared/Routes';
import {
    AdminMetricsQuery,
    useAdminMetricsQuery,
    useImportNotificationsMutation
} from 'types/gqlReactTypings.generated.d';
import {PageHeader} from '../../components/PageHeader';
import {FileDropzone} from "../../components/fileDropzone";

gql`
    query AdminMetrics {
        adminMetrics {
            userCount
            feedPostCount
            teamCount
            liveStreamCount
            usageCards {
                key
                label
                faIcon
                color
                total
                quarter {
                    count
                    previousCount
                    percentChange
                }
                month {
                    count
                    previousCount
                    percentChange
                }
                week {
                    count
                    previousCount
                    percentChange
                }
                day {
                    count
                    previousCount
                    percentChange
                }
            }
        }
    }
`

type UsageMetric = AdminMetricsQuery['adminMetrics']['usageCards'][number];
type MetricPeriod = UsageMetric['quarter'];

const numberFormatter = new Intl.NumberFormat();

const metricRoutes: Partial<Record<UsageMetric['key'], string>> = {
    users: AdminRoutes.USERS,
    teams: AdminRoutes.TEAMS,
    feedPosts: AdminRoutes.FEED_POSTS,
    liveStreams: AdminRoutes.LIVE_STREAMS,
};

const formatPercent = (percentChange?: number): string => {
    if (percentChange == null) return 'New';
    return `${percentChange > 0 ? '+' : ''}${percentChange}%`;
}

const MetricPeriodValue: React.FC<{ label: string; period: MetricPeriod }> = ({label, period}) => {
    const trendColor = period.percentChange == null
        ? 'text-muted'
        : period.percentChange >= 0 ? 'text-success' : 'text-danger';

    return (
        <div className='d-flex justify-content-between align-items-center py-1'>
            <span className='text-xs text-uppercase text-gray-600'>{label}</span>
            <span className='text-right'>
                <strong className='text-gray-800 mr-2'>+{numberFormatter.format(period.count)}</strong>
                <span
                    className={`text-xs font-weight-bold ${trendColor}`}
                    title={`Previous period: ${numberFormatter.format(period.previousCount)}`}
                >
                    {formatPercent(period.percentChange)}
                </span>
            </span>
        </div>
    );
}

const UsageMetricCard: React.FC<{ metric: UsageMetric }> = ({metric}) => {
    const route = metricRoutes[metric.key];
    const content = (
        <div
            className={`card border-left-${metric.color} shadow h-100 py-2`}
            style={{width: '100%', textAlign: 'left'}}
        >
            <div className='card-body'>
                <div className='d-flex justify-content-between align-items-start mb-3'>
                    <div>
                        <div className={`text-xs font-weight-bold text-${metric.color} text-uppercase mb-1`}>
                            {metric.label}
                        </div>
                        <div className='h4 mb-0 font-weight-bold text-gray-800'>
                            {numberFormatter.format(metric.total)}
                        </div>
                    </div>
                    <i className={`fas fa-${metric.faIcon} fa-2x text-gray-300`}/>
                </div>
                <div className='border-top pt-2'>
                    <MetricPeriodValue label='Quarter' period={metric.quarter}/>
                    <MetricPeriodValue label='Month' period={metric.month}/>
                    <MetricPeriodValue label='Week' period={metric.week}/>
                    <MetricPeriodValue label='Day' period={metric.day}/>
                </div>
            </div>
        </div>
    );

    return (
        <div className='col-xl-3 col-lg-4 col-md-6 mb-4'>
            {route
                ? <Link to={route} className='d-block h-100 text-decoration-none'>{content}</Link>
                : content}
        </div>
    );
}

export const AdminDashboard: React.FC = () => {
    const adminMetricsQuery = useAdminMetricsQuery();
    const [showImportModal, setShowImportModal] = useState(false);
    const [file, setFile] = useState<string | undefined>();
    const [importMutation] = useImportNotificationsMutation();
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const [successMessage, setSuccessMessage] = useState<string | undefined>();
    const importNotifications = async () => {
        const res = await importMutation({variables: {fileName: file}});
        if (res.errors) {
            setErrorMessage(res.errors.map((e) => e.message).join("\n"));
        } else {
            setSuccessMessage(res.data?.importNotifications.message);
            // setShowImportModal(false);
            // alert(res.data?.importNotifications.message);
        }
    }
    return (
        <div>
            <Modal show={showImportModal} onHide={() => {
                setErrorMessage(undefined);
                setFile(undefined);
                setSuccessMessage(undefined);
                setShowImportModal(false);
            }}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Imprt Notifications</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row style={{marginBottom: 20}}>
                        <Col md={12}>
                            {successMessage && <h3 style={{color: 'green'}}>{successMessage}</h3>}
                            {errorMessage && <h3 style={{color: 'red'}}>{errorMessage}</h3>}
                        </Col>
                        {!successMessage && <>
                            <Col md={12}>
                                <FileDropzone
                                    onSuccess={(file, fileName, fileKey) => {
                                        setFile(fileName)
                                    }}
                                    accept={{'text/csv': []}}
                                />
                            </Col>
                            <Col md={12} style={{marginTop: 20}}>
                                <div>{file}</div>
                            </Col>
                        </>}
                    </Row>

                </Modal.Body>
                <Modal.Footer>
                    {!successMessage && <Button variant='success' onClick={importNotifications}>Import</Button>}
                </Modal.Footer>
            </Modal>
            <PageHeader
                title='Dashboard'
                rightItem={<Button variant="primary" onClick={() => setShowImportModal(true)}>Import
                    Notifications</Button>}
            />

            <GqlQueryRender<AdminMetricsQuery> query={adminMetricsQuery}>
                {({adminMetrics}) => (
                    <Row>
                        {adminMetrics.usageCards.map((metric) => (
                            <UsageMetricCard key={metric.key} metric={metric}/>
                        ))}
                    </Row>
                )}
            </GqlQueryRender>
        </div>
    );
}
